import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiActivity, FiAward } from 'react-icons/fi';
import { Button } from '@/components/common/Button';
import { mockDashboardData } from '@/data/mockData';

const Container = styled.div`
  height: 100vh;
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.xl};
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  flex-shrink: 0;
`;

const BackButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontSizes['3xl']};
  color: ${({ theme }) => theme.colors.onBackground};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  flex: 1;
`;

const ContentLayout = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${({ theme }) => theme.spacing.lg};
  overflow: hidden;
`;

const WorkstreamsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  overflow: hidden;
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  color: ${({ theme }) => theme.colors.onBackground};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  flex-shrink: 0;
`;

const WorkstreamsList = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  overflow-y: auto;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.surface};
    border-radius: ${({ theme }) => theme.borderRadius.md};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borderRadius.md};

    &:hover {
      background: ${({ theme }) => theme.colors.muted};
    }
  }
`;

const WorkstreamCard = styled(motion.div)`
  background: ${({ theme }) => 
    theme.colors.background === '#0f172a' 
      ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(15, 23, 42, 0.9) 100%)'
      : 'rgba(255, 255, 255, 0.7)'};
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  border: 1px solid ${({ theme }) => 
    theme.colors.background === '#0f172a' 
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(255, 255, 255, 0.18)'};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.lg};
  transition: ${({ theme }) => theme.transitions.default};

  &:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
    background: ${({ theme }) => 
      theme.colors.background === '#0f172a' 
        ? 'linear-gradient(135deg, rgba(51, 65, 85, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)'
        : 'rgba(255, 255, 255, 0.85)'};
  }
`;

const RAGIndicator = styled.div<{ status: 'red' | 'amber' | 'green' }>`
  width: 56px;
  height: 56px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ status }) => {
    switch (status) {
      case 'red':
        return '#ef4444';
      case 'amber':
        return '#f59e0b';
      case 'green':
        return '#10b981';
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px ${({ status }) => {
    switch (status) {
      case 'red':
        return '#ef444440';
      case 'amber':
        return '#f59e0b40';
      case 'green':
        return '#10b98140';
    }
  }};

  svg {
    width: 28px;
    height: 28px;
    color: white;
  }
`;

const WorkstreamInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const WorkstreamName = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  color: ${({ theme }) => theme.colors.onBackground};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const WorkstreamDescription = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.muted};
  line-height: 1.5;
  margin: 0;
`;

const StatusLabel = styled.div<{ status: 'red' | 'amber' | 'green' }>`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  text-transform: uppercase;
  background: ${({ status }) => {
    switch (status) {
      case 'red':
        return '#ef444415';
      case 'amber':
        return '#f59e0b15';
      case 'green':
        return '#10b98115';
    }
  }};
  color: ${({ status }) => {
    switch (status) {
      case 'red':
        return '#ef4444';
      case 'amber':
        return '#f59e0b';
      case 'green':
        return '#10b981';
    }
  }};
  flex-shrink: 0;
`;

const SidePanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const ScoreCard = styled(motion.div)`
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.primary} 0%, ${({ theme }) => theme.colors.primaryDark} 100%);
  color: white;
  padding: ${({ theme }) => theme.spacing.xl};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  text-align: center;
`;

const ScoreIcon = styled.div`
  width: 64px;
  height: 64px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: ${({ theme }) => theme.borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${({ theme }) => theme.spacing.md};

  svg {
    width: 32px;
    height: 32px;
  }
`;

const ScoreLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  opacity: 0.9;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const ScoreValue = styled.div`
  font-size: 4rem;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  line-height: 1;
`;

const ContributionCard = styled(motion.div)`
  background: ${({ theme }) => 
    theme.colors.background === '#0f172a' 
      ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(15, 23, 42, 0.9) 100%)'
      : 'rgba(255, 255, 255, 0.7)'};
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing.xl};
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  border: 1px solid ${({ theme }) => theme.colors.success}40;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ContributionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const ContributionIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme }) => theme.colors.success}15;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 24px;
    height: 24px;
    color: ${({ theme }) => theme.colors.success};
  }
`;

const ContributionTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  color: ${({ theme }) => theme.colors.onBackground};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
`;

const ContributionText = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.onSurface};
  line-height: 1.7;
  margin: 0;
`;

export const DashboardPage = () => {
  const navigate = useNavigate();

  return (
    <Container>
      <Header>
        <BackButton variant="outline" onClick={() => navigate('/')}>
          <FiArrowLeft /> Back
        </BackButton>
        <Title>Status Dashboard</Title>
      </Header>

      <ContentLayout>
        <WorkstreamsSection>
          <SectionTitle>Workstream Health</SectionTitle>
          <WorkstreamsList>
            {mockDashboardData.workstreams.map((workstream, index) => (
              <WorkstreamCard
                key={workstream.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <RAGIndicator status={workstream.status}>
                  <FiActivity />
                </RAGIndicator>
                <WorkstreamInfo>
                  <WorkstreamName>{workstream.name}</WorkstreamName>
                  <WorkstreamDescription>
                    {workstream.description}
                  </WorkstreamDescription>
                </WorkstreamInfo>
                <StatusLabel status={workstream.status}>
                  {workstream.status}
                </StatusLabel>
              </WorkstreamCard>
            ))}
          </WorkstreamsList>
        </WorkstreamsSection>

        <SidePanel>
          <ScoreCard
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
          >
            <ScoreIcon>
              <FiAward />
            </ScoreIcon>
            <ScoreLabel>Weekly Score</ScoreLabel>
            <ScoreValue>{mockDashboardData.weeklyScore}</ScoreValue>
          </ScoreCard>

          <ContributionCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <ContributionHeader>
              <ContributionIcon>
                <FiAward />
              </ContributionIcon>
              <ContributionTitle>Your Contribution</ContributionTitle>
            </ContributionHeader>
            <ContributionText>
              {mockDashboardData.yourContribution}
            </ContributionText>
          </ContributionCard>
        </SidePanel>
      </ContentLayout>
    </Container>
  );
};
